import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, ArrowLeft } from 'lucide-react';
import AssetsList from './review/AssetsList';
import AssetPreview from './review/AssetPreview';
import axiosInstance from '../config/axios';
import { API_BASE_URL, type FeedbackPin } from '../types/review';

/**
 * ContentPreview - Internal preview for content items
 * This is different from ReviewPageFinal which is for client/AM reviews
 * This is for team members to preview content internally with full asset preview capabilities
 */
export default function ContentPreview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Core content state
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [selectedTab, setSelectedTab] = useState('all');

  // Feedback/review state (for AssetPreview compatibility)
  const [feedbackPins, setFeedbackPins] = useState<Record<number, FeedbackPin[]>>({});
  const [isPlacingPin, setIsPlacingPin] = useState(false);
  const [tempPin, setTempPin] = useState<{ x: number; y: number } | null>(null);
  const [pinComment, setPinComment] = useState('');

  useEffect(() => {
    fetchContent();
  }, [id]);

  const fetchContent = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await axiosInstance.get(`/content/${id}`);
      const contentData = response.data;

      setContent(contentData);

      // Auto-select first asset if available
      if (contentData.assets && contentData.assets.length > 0) {
        setSelectedAsset(contentData.assets[0]);
      }

      setError(null);
    } catch (err) {
      console.error('Failed to fetch content:', err);
      setError('Failed to load content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate(-1); // Go back to previous page
  };

  // Handler for placing feedback pins (optional for internal use)
  const handlePlacePin = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isPlacingPin || !selectedAsset) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setTempPin({ x, y });
  };

  // Handler for adding a feedback pin
  const handleAddPin = () => {
    if (!tempPin || !pinComment || !selectedAsset) return;

    const newPin: FeedbackPin = {
      id: Date.now().toString(),
      x: tempPin.x,
      y: tempPin.y,
      comment: pinComment,
      author: 'Team Member',
      timestamp: new Date()
    };

    setFeedbackPins(prev => ({
      ...prev,
      [selectedAsset.id]: [...(prev[selectedAsset.id] || []), newPin]
    }));

    // Reset pin placement state
    setTempPin(null);
    setPinComment('');
    setIsPlacingPin(false);
  };

  // Handler for canceling pin placement
  const handleCancelPin = () => {
    setTempPin(null);
    setPinComment('');
    setIsPlacingPin(false);
  };

  // Handler for saving asset changes
  const handleSaveAsset = async (assetId: number, newContent: string) => {
    try {
      await axiosInstance.put(`/assets/${assetId}`, {
        content: newContent
      });
      // Refresh content to get updated data
      await fetchContent();
    } catch (err) {
      console.error('Failed to save asset:', err);
      throw err; // Let the AssetPreview component handle the error
    }
  };

  // Handler for approving asset (optional - for completeness)
  const handleApproveAsset = async (assetId: number) => {
    console.log('Approve asset:', assetId);
    // Internal preview might not need approval workflow
  };

  // Handler for rejecting asset (optional - for completeness)
  const handleRejectAsset = async (assetId: number) => {
    console.log('Reject asset:', assetId);
    // Internal preview might not need rejection workflow
  };

  // Handler for editing asset (optional - for completeness)
  const handleEditAsset = (assetId: number) => {
    console.log('Edit asset:', assetId);
    // Could navigate to asset editor or enable edit mode
  };

  // Handler for approving all assets (optional - for completeness)
  const handleApproveAll = () => {
    console.log('Approve all assets');
    // Internal preview might not need bulk approval
  };

  // Parse metadata if it's a string
  const getContentMetadata = () => {
    if (!content?.metadata) return undefined;
    if (typeof content.metadata === 'string') {
      try {
        return JSON.parse(content.metadata);
      } catch {
        return undefined;
      }
    }
    return content.metadata;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading content...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700 mb-6">{error || 'Content not found'}</p>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Go back"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{content.title}</h1>
              <p className="text-sm text-gray-500">
                {content.type} • Preview Mode
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close preview"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Assets List */}
        {content.assets && content.assets.length > 0 && (
          <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
            <AssetsList
              assets={content.assets}
              selectedAsset={selectedAsset}
              onSelectAsset={setSelectedAsset}
              selectedTab={selectedTab}
              onTabChange={setSelectedTab}
            />
          </div>
        )}

        {/* Center - Asset Preview with full functionality */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {selectedAsset ? (
            <div className="h-full">
              <AssetPreview
                selectedAsset={selectedAsset}
                feedbackPins={feedbackPins[selectedAsset.id] || []}
                isPlacingPin={isPlacingPin}
                tempPin={tempPin}
                onPlacePin={handlePlacePin}
                apiBaseUrl={API_BASE_URL}
                reviewType="account_manager"
                onApproveAsset={handleApproveAsset}
                onRejectAsset={handleRejectAsset}
                onEditAsset={handleEditAsset}
                onSaveAsset={handleSaveAsset}
                onApproveAll={handleApproveAll}
                setPinComment={setPinComment}
                pinComment={pinComment}
                onAddPin={handleAddPin}
                onCancelPin={handleCancelPin}
                contentType={content.type}
                contentMetadata={getContentMetadata()}
                allAssets={content.assets || []}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <p className="text-lg">No assets available</p>
                <p className="text-sm mt-2">This content item doesn't have any assets yet.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
